import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Settings, Shield, Plus, Trash2, Edit2, Check, X,
  Mail, Lock, AlertTriangle, Save, Loader2, RefreshCw,
  Key, Brain, Server, MessageSquare, Sliders, Building2,
  Upload, Download, FileSpreadsheet, Clock, Play, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Slider } from '../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAdminStore, useAuthStore, useUIStore, useDepartmentStore, useTeamStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const { user } = useAuthStore();
  const { users, settings, fetchUsers, createUser, updateUser, deleteUser, fetchSettings, updateSettings } = useAdminStore();
  const { departments, fetchDepartments, createDepartment } = useDepartmentStore();
  const { teams, fetchTeams } = useTeamStore();
  const { language } = useUIStore();
  const { t } = useTranslation(language);
  
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', is_admin: false, department_id: '' });
  const [localSettings, setLocalSettings] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Department creation inline
  const [showNewDeptForm, setShowNewDeptForm] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [selectedTeamForDept, setSelectedTeamForDept] = useState('');
  
  // Bulk upload
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  
  // Performance logic - role selection
  const [selectedPerformanceRole, setSelectedPerformanceRole] = useState('project_lead');

  // Scheduler state
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [isLoadingScheduler, setIsLoadingScheduler] = useState(false);
  const [schedulerLogs, setSchedulerLogs] = useState([]);
  const [availableTimezones, setAvailableTimezones] = useState([]);

  // Default performance settings per role
  const defaultRolePerformance = {
    project_lead: { completion_weight: 30, on_time_weight: 25, engagement_weight: 25, consistency_weight: 20, description: 'Focus on team coordination and project delivery' },
    project_manager: { completion_weight: 35, on_time_weight: 30, engagement_weight: 20, consistency_weight: 15, description: 'Balance between execution and oversight' },
    contributor: { completion_weight: 50, on_time_weight: 25, engagement_weight: 15, consistency_weight: 10, description: 'Primary focus on task completion' }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (settings) {
      setLocalSettings(settings);
    }
  }, [settings]);

  const loadData = async () => {
    try {
      await Promise.all([fetchUsers(), fetchSettings(), fetchDepartments(), fetchTeams()]);
      loadSchedulerStatus();
    } catch (error) {
      toast.error(error.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSchedulerStatus = async () => {
    try {
      const token = useAuthStore.getState().token;
      const res = await fetch(`${API_URL}/api/admin/scheduler/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSchedulerStatus(data);
      }
      
      // Also load available timezones
      const tzRes = await fetch(`${API_URL}/api/admin/scheduler/timezones`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (tzRes.ok) {
        const tzData = await tzRes.json();
        setAvailableTimezones(tzData.timezones || []);
      }
    } catch (error) {
      console.error('Failed to load scheduler status:', error);
    }
  };

  const loadSchedulerLogs = async () => {
    try {
      const token = useAuthStore.getState().token;
      const res = await fetch(`${API_URL}/api/admin/scheduler/logs?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSchedulerLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Failed to load scheduler logs:', error);
    }
  };

  const handleUpdateScheduler = async () => {
    setIsLoadingScheduler(true);
    try {
      const token = useAuthStore.getState().token;
      const params = new URLSearchParams({
        enabled: localSettings?.deadline_reminder_enabled ?? true,
        reminder_days: localSettings?.deadline_reminder_days || 3,
        scheduled_time: localSettings?.deadline_reminder_time || '08:00',
        timezone_str: localSettings?.deadline_reminder_timezone || 'UTC'
      });
      
      const res = await fetch(`${API_URL}/api/admin/scheduler/update?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Scheduler settings updated');
        loadSchedulerStatus();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to update scheduler');
      }
    } catch (error) {
      toast.error('Failed to update scheduler');
    } finally {
      setIsLoadingScheduler(false);
    }
  };

  const handleRunSchedulerNow = async () => {
    setIsLoadingScheduler(true);
    try {
      const token = useAuthStore.getState().token;
      const res = await fetch(`${API_URL}/api/admin/scheduler/run-now`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Deadline digest job triggered! Check logs for results.');
        setTimeout(() => loadSchedulerLogs(), 5000); // Refresh logs after 5 seconds
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to trigger job');
      }
    } catch (error) {
      toast.error('Failed to trigger scheduler');
    } finally {
      setIsLoadingScheduler(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      const createdUser = await createUser(newUser);
      
      // If department selected, add user to department
      if (newUser.department_id && newUser.department_id !== 'none') {
        try {
          const token = localStorage.getItem('token');
          await fetch(`${API_URL}/api/departments/${newUser.department_id}/members`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ user_id: createdUser.id })
          });
        } catch (e) {
          console.error('Failed to add user to department:', e);
        }
      }
      
      toast.success('User created successfully');
      setShowCreateUser(false);
      setNewUser({ email: '', password: '', name: '', is_admin: false, department_id: '' });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreateDepartmentInline = async () => {
    if (!newDeptName || !selectedTeamForDept) {
      toast.error('Please enter department name and select a team');
      return;
    }
    try {
      await createDepartment(newDeptName, '', selectedTeamForDept);
      toast.success('Department created');
      setShowNewDeptForm(false);
      setNewDeptName('');
      setSelectedTeamForDept('');
      fetchDepartments();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await updateUser(userId, updates);
      toast.success('User updated');
      setEditingUser(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await deleteUser(userId);
      toast.success('User deleted');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await updateSettings(localSettings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const downloadCSVTemplate = () => {
    const csv = 'name,email,password,department_name\nJohn Doe,john@example.com,password123,Engineering\nJane Smith,jane@example.com,pass456,Marketing';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    setUploadResult(null);
    
    try {
      const token = useAuthStore.getState().token;
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/admin/users/bulk-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Upload failed');
      }
      
      const data = await res.json();
      setUploadResult(data);
      if (data.created_count > 0) {
        toast.success(`${data.created_count} users created`);
        await fetchUsers();
      }
      if (data.error_count > 0) {
        toast.error(`${data.error_count} rows had errors`);
      }
    } catch (error) {
      toast.error(error.message || 'Upload failed');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="glass max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">
              You need admin privileges to access this page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            Admin Settings
          </h1>
          <p className="text-muted-foreground">
            Manage users, integrations, and system settings
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="glass">
          <TabsTrigger value="users" className="gap-2">
            <Users className="h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="integrations" className="gap-2">
            <Key className="h-4 w-4" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="performance" className="gap-2">
            <Sliders className="h-4 w-4" />
            Performance Logic
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-3">
            <h2 className="font-heading text-lg font-semibold">User Management</h2>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={downloadCSVTemplate} className="gap-2">
                <Download className="h-4 w-4" />
                CSV Template
              </Button>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="gap-2"
              >
                {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Bulk Upload
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleBulkUpload}
                className="hidden"
              />
              <Button onClick={() => setShowCreateUser(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Add User
              </Button>
            </div>
          </div>
          
          {/* Upload Results */}
          {uploadResult && (
            <Card className={uploadResult.error_count > 0 ? 'border-destructive/50' : 'border-chart-3/50'}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4 mb-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  <span className="font-medium">Upload Results</span>
                  <Button variant="ghost" size="sm" onClick={() => setUploadResult(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm">
                  <span className="text-chart-3">{uploadResult.created_count} created</span>
                  {uploadResult.error_count > 0 && (
                    <span className="text-destructive ml-3">{uploadResult.error_count} errors</span>
                  )}
                </p>
                {uploadResult.errors?.length > 0 && (
                  <div className="mt-2 text-xs text-muted-foreground max-h-24 overflow-auto">
                    {uploadResult.errors.map((err, i) => (
                      <div key={i}>Row {err.row}: {err.error}</div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="glass">
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                <div className="divide-y divide-white/10">
                  {users.map((u) => (
                    <motion.div
                      key={u.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex items-center justify-between p-4 hover:bg-white/5"
                    >
                      <div className="flex items-center gap-4">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-primary/20 text-primary">
                            {u.name?.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{u.name}</p>
                          <p className="text-sm text-muted-foreground">{u.email}</p>
                        </div>
                        {u.is_admin && (
                          <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">
                            Admin
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {u.id !== user.id && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingUser(u)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteUser(u.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-4">
          <h2 className="font-heading text-lg font-semibold">Integration Settings</h2>

          <div className="grid gap-4">
            {/* AI Provider */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Brain className="h-5 w-5 text-primary" />
                  AI Provider
                </CardTitle>
                <CardDescription>
                  Choose the AI provider for chat and suggestions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Select 
                    value={localSettings?.ai_provider || 'emergent'}
                    onValueChange={(v) => setLocalSettings({ ...localSettings, ai_provider: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="emergent">Emergent (Default)</SelectItem>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="gemini">Google Gemini</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {localSettings?.ai_provider === 'openai' && (
                  <div className="space-y-2">
                    <Label>OpenAI API Key</Label>
                    <Input
                      type="password"
                      placeholder="sk-..."
                      value={localSettings?.openai_api_key || ''}
                      onChange={(e) => setLocalSettings({ ...localSettings, openai_api_key: e.target.value })}
                    />
                  </div>
                )}

                {localSettings?.ai_provider === 'gemini' && (
                  <div className="space-y-2">
                    <Label>Gemini API Key</Label>
                    <Input
                      type="password"
                      placeholder="AIza..."
                      value={localSettings?.gemini_api_key || ''}
                      onChange={(e) => setLocalSettings({ ...localSettings, gemini_api_key: e.target.value })}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SMTP Settings */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Mail className="h-5 w-5 text-chart-4" />
                  Email Notifications (SMTP)
                </CardTitle>
                <CardDescription>
                  Configure email for task assignments and approvals
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>SMTP Host</Label>
                    <Input
                      placeholder="smtp.example.com"
                      value={localSettings?.smtp_host || ''}
                      onChange={(e) => setLocalSettings({ ...localSettings, smtp_host: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SMTP Port</Label>
                    <Input
                      type="number"
                      placeholder="587"
                      value={localSettings?.smtp_port || ''}
                      onChange={(e) => setLocalSettings({ ...localSettings, smtp_port: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input
                      placeholder="user@example.com"
                      value={localSettings?.smtp_user || ''}
                      onChange={(e) => setLocalSettings({ ...localSettings, smtp_user: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Password</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={localSettings?.smtp_password || ''}
                      onChange={(e) => setLocalSettings({ ...localSettings, smtp_password: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>From Email</Label>
                  <Input
                    placeholder="noreply@yourcompany.com"
                    value={localSettings?.smtp_from_email || ''}
                    onChange={(e) => setLocalSettings({ ...localSettings, smtp_from_email: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/20">
                    <div>
                      <p className="text-sm font-medium">Use TLS</p>
                      <p className="text-xs text-muted-foreground">STARTTLS (Port 587)</p>
                    </div>
                    <Switch
                      checked={localSettings?.smtp_use_tls ?? true}
                      onCheckedChange={(checked) => setLocalSettings({ 
                        ...localSettings, 
                        smtp_use_tls: checked,
                        smtp_use_ssl: checked ? false : localSettings?.smtp_use_ssl 
                      })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/20">
                    <div>
                      <p className="text-sm font-medium">Use SSL</p>
                      <p className="text-xs text-muted-foreground">Implicit SSL (Port 465)</p>
                    </div>
                    <Switch
                      checked={localSettings?.smtp_use_ssl ?? false}
                      onCheckedChange={(checked) => setLocalSettings({ 
                        ...localSettings, 
                        smtp_use_ssl: checked,
                        smtp_use_tls: checked ? false : localSettings?.smtp_use_tls 
                      })}
                    />
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/test-email`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const data = await res.json();
                      if (res.ok) {
                        toast.success(`Test email sent to ${data.to}`);
                      } else {
                        toast.error(data.detail || 'Failed to send test email');
                      }
                    } catch (error) {
                      toast.error('Failed to send test email');
                    }
                  }}
                  className="mt-2"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Send Test Email
                </Button>
              </CardContent>
            </Card>

            {/* Email Notification Preferences */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Settings className="h-5 w-5 text-chart-2" />
                  Email Notification Preferences
                </CardTitle>
                <CardDescription>
                  Choose which events trigger email notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4">
                  {[
                    { key: 'task_assigned', label: 'Task Assigned', desc: 'When a task is assigned to someone' },
                    { key: 'task_completed', label: 'Task Completed', desc: 'When a task is marked as done' },
                    { key: 'task_comment', label: 'Task Comments', desc: 'When someone comments on a task' },
                    { key: 'approval_requested', label: 'Approval Requested', desc: 'When approval is needed for changes' },
                    { key: 'approval_resolved', label: 'Approval Resolved', desc: 'When an approval is approved/rejected' },
                    { key: 'status_changed', label: 'Status Changed', desc: 'When project/task status changes' },
                    { key: 'deadline_reminder', label: 'Deadline Reminders', desc: 'Reminders for upcoming deadlines' },
                    { key: 'project_member_added', label: 'Project Member Added', desc: 'When added to a project' },
                  ].map(({ key, label, desc }) => (
                    <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-secondary/20 border border-white/5">
                      <div>
                        <p className="font-medium text-sm">{label}</p>
                        <p className="text-xs text-muted-foreground">{desc}</p>
                      </div>
                      <Switch
                        checked={localSettings?.email_notifications?.[key] ?? true}
                        onCheckedChange={(checked) => setLocalSettings({
                          ...localSettings,
                          email_notifications: {
                            ...localSettings?.email_notifications,
                            [key]: checked
                          }
                        })}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Microsoft Teams */}
            <Card className="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <MessageSquare className="h-5 w-5 text-chart-1" />
                  Microsoft Teams Bot
                </CardTitle>
                <CardDescription>
                  Connect to Microsoft Teams for notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Teams Webhook URL</Label>
                  <Input
                    placeholder="https://outlook.office.com/webhook/..."
                    value={localSettings?.teams_webhook_url || ''}
                    onChange={(e) => setLocalSettings({ ...localSettings, teams_webhook_url: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bot ID (Optional)</Label>
                  <Input
                    placeholder="Bot ID from Azure"
                    value={localSettings?.teams_bot_id || ''}
                    onChange={(e) => setLocalSettings({ ...localSettings, teams_bot_id: e.target.value })}
                  />
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/test-teams`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const data = await res.json();
                      if (res.ok) {
                        toast.success('Teams notification sent!');
                      } else {
                        toast.error(data.detail || 'Failed to send Teams notification');
                      }
                    } catch (error) {
                      toast.error('Failed to send Teams notification');
                    }
                  }}
                  className="mt-2"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Test Teams Connection
                </Button>
              </CardContent>
            </Card>

            {/* Deadline Reminder Scheduler */}
            <Card className="glass border-chart-3/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Clock className="h-5 w-5 text-chart-3" />
                  Deadline Reminder Scheduler
                </CardTitle>
                <CardDescription>
                  Automated daily digest of upcoming task deadlines
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Enable/Disable */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/20">
                  <div>
                    <p className="font-medium">Enable Daily Digest</p>
                    <p className="text-xs text-muted-foreground">Send deadline reminders to all users</p>
                  </div>
                  <Switch
                    checked={localSettings?.deadline_reminder_enabled ?? true}
                    onCheckedChange={(checked) => setLocalSettings({ 
                      ...localSettings, 
                      deadline_reminder_enabled: checked 
                    })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Days Before Deadline</Label>
                    <Select
                      value={String(localSettings?.deadline_reminder_days || 3)}
                      onValueChange={(v) => setLocalSettings({ 
                        ...localSettings, 
                        deadline_reminder_days: parseInt(v) 
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 day</SelectItem>
                        <SelectItem value="2">2 days</SelectItem>
                        <SelectItem value="3">3 days</SelectItem>
                        <SelectItem value="5">5 days</SelectItem>
                        <SelectItem value="7">7 days (1 week)</SelectItem>
                        <SelectItem value="14">14 days (2 weeks)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Include tasks due within this period</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Send Time</Label>
                    <Select
                      value={localSettings?.deadline_reminder_time || '08:00'}
                      onValueChange={(v) => setLocalSettings({ 
                        ...localSettings, 
                        deadline_reminder_time: v 
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="06:00">6:00 AM</SelectItem>
                        <SelectItem value="07:00">7:00 AM</SelectItem>
                        <SelectItem value="08:00">8:00 AM</SelectItem>
                        <SelectItem value="09:00">9:00 AM</SelectItem>
                        <SelectItem value="10:00">10:00 AM</SelectItem>
                        <SelectItem value="12:00">12:00 PM</SelectItem>
                        <SelectItem value="18:00">6:00 PM</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Daily digest send time</p>
                  </div>
                </div>

                {/* Timezone selector */}
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select
                    value={localSettings?.deadline_reminder_timezone || 'UTC'}
                    onValueChange={(v) => setLocalSettings({ 
                      ...localSettings, 
                      deadline_reminder_timezone: v 
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTimezones.length > 0 ? (
                        availableTimezones.map((tz) => (
                          <SelectItem key={tz.value} value={tz.value}>
                            {tz.label} ({tz.offset})
                          </SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="UTC">UTC (Coordinated Universal Time)</SelectItem>
                          <SelectItem value="America/New_York">New York (Eastern)</SelectItem>
                          <SelectItem value="America/Los_Angeles">Los Angeles (Pacific)</SelectItem>
                          <SelectItem value="Europe/London">London (GMT)</SelectItem>
                          <SelectItem value="Europe/Paris">Paris (CET)</SelectItem>
                          <SelectItem value="Asia/Kolkata">India (IST)</SelectItem>
                          <SelectItem value="Asia/Tokyo">Tokyo (JST)</SelectItem>
                          <SelectItem value="Australia/Sydney">Sydney (AEDT)</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Timezone for scheduled send time</p>
                </div>

                {/* Status */}
                {schedulerStatus && (
                  <div className="p-3 rounded-lg bg-secondary/30 border border-white/5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Scheduler Status</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${schedulerStatus.job_active ? 'bg-chart-3/20 text-chart-3' : 'bg-muted text-muted-foreground'}`}>
                        {schedulerStatus.job_active ? '● Active' : '○ Inactive'}
                      </span>
                    </div>
                    {schedulerStatus.next_run && (
                      <p className="text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3 inline mr-1" />
                        Next run: {new Date(schedulerStatus.next_run).toLocaleString()} ({schedulerStatus.timezone || 'UTC'})
                      </p>
                    )}
                    {schedulerStatus.last_execution && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Last run: {new Date(schedulerStatus.last_execution.executed_at).toLocaleString()} 
                        ({schedulerStatus.last_execution.emails_sent} emails sent)
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleUpdateScheduler}
                    disabled={isLoadingScheduler}
                  >
                    {isLoadingScheduler ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Schedule
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleRunSchedulerNow}
                    disabled={isLoadingScheduler}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Run Now
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => { loadSchedulerStatus(); loadSchedulerLogs(); }}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <Button onClick={handleSaveSettings} disabled={isSaving} className="gap-2">
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Integration Settings
          </Button>
        </TabsContent>

        {/* Performance Logic Tab */}
        <TabsContent value="performance" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-lg font-semibold">Role-Based Performance Logic</h2>
              <p className="text-muted-foreground text-sm">
                Customize how productivity scores are calculated for each role.
              </p>
            </div>
          </div>

          {/* Role Selector */}
          <div className="flex gap-2">
            {['project_lead', 'project_manager', 'contributor'].map((role) => (
              <Button
                key={role}
                variant={selectedPerformanceRole === role ? 'default' : 'outline'}
                onClick={() => setSelectedPerformanceRole(role)}
                className="capitalize"
              >
                {role === 'project_lead' ? 'Project Lead' : role === 'project_manager' ? 'Project Manager' : 'Contributor'}
              </Button>
            ))}
          </div>

          {/* Role Description */}
          <Card className="glass border-primary/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <span className="font-medium capitalize">
                  {selectedPerformanceRole === 'project_lead' ? 'Project Lead' : 
                   selectedPerformanceRole === 'project_manager' ? 'Project Manager' : 'Contributor'}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {localSettings?.performance_logic?.[selectedPerformanceRole]?.description || 
                 defaultRolePerformance[selectedPerformanceRole]?.description}
              </p>
            </CardContent>
          </Card>

          {/* Performance Weights for Selected Role */}
          <Card className="glass">
            <CardContent className="p-6 space-y-6">
              {/* Completion Weight */}
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label>Task Completion Weight</Label>
                  <span className="text-primary font-medium">
                    {localSettings?.performance_logic?.[selectedPerformanceRole]?.completion_weight || 
                     defaultRolePerformance[selectedPerformanceRole]?.completion_weight}%
                  </span>
                </div>
                <Slider
                  value={[localSettings?.performance_logic?.[selectedPerformanceRole]?.completion_weight || 
                         defaultRolePerformance[selectedPerformanceRole]?.completion_weight]}
                  onValueChange={(v) => setLocalSettings({
                    ...localSettings,
                    performance_logic: {
                      ...localSettings?.performance_logic,
                      [selectedPerformanceRole]: {
                        ...localSettings?.performance_logic?.[selectedPerformanceRole],
                        completion_weight: v[0]
                      }
                    }
                  })}
                  max={100}
                  step={5}
                />
                <p className="text-xs text-muted-foreground">
                  Weight given to completed tasks vs total assigned
                </p>
              </div>

              {/* On-time Weight */}
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label>On-Time Delivery Weight</Label>
                  <span className="text-primary font-medium">
                    {localSettings?.performance_logic?.[selectedPerformanceRole]?.on_time_weight || 
                     defaultRolePerformance[selectedPerformanceRole]?.on_time_weight}%
                  </span>
                </div>
                <Slider
                  value={[localSettings?.performance_logic?.[selectedPerformanceRole]?.on_time_weight || 
                         defaultRolePerformance[selectedPerformanceRole]?.on_time_weight]}
                  onValueChange={(v) => setLocalSettings({
                    ...localSettings,
                    performance_logic: {
                      ...localSettings?.performance_logic,
                      [selectedPerformanceRole]: {
                        ...localSettings?.performance_logic?.[selectedPerformanceRole],
                        on_time_weight: v[0]
                      }
                    }
                  })}
                  max={100}
                  step={5}
                />
                <p className="text-xs text-muted-foreground">
                  Bonus for tasks completed before due date
                </p>
              </div>

              {/* Engagement Weight */}
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label>Engagement Weight</Label>
                  <span className="text-primary font-medium">
                    {localSettings?.performance_logic?.[selectedPerformanceRole]?.engagement_weight || 
                     defaultRolePerformance[selectedPerformanceRole]?.engagement_weight}%
                  </span>
                </div>
                <Slider
                  value={[localSettings?.performance_logic?.[selectedPerformanceRole]?.engagement_weight || 
                         defaultRolePerformance[selectedPerformanceRole]?.engagement_weight]}
                  onValueChange={(v) => setLocalSettings({
                    ...localSettings,
                    performance_logic: {
                      ...localSettings?.performance_logic,
                      [selectedPerformanceRole]: {
                        ...localSettings?.performance_logic?.[selectedPerformanceRole],
                        engagement_weight: v[0]
                      }
                    }
                  })}
                  max={100}
                  step={5}
                />
                <p className="text-xs text-muted-foreground">
                  Activity level - comments, updates, collaboration
                </p>
              </div>

              {/* Consistency Weight */}
              <div className="space-y-3">
                <div className="flex justify-between">
                  <Label>Consistency Weight</Label>
                  <span className="text-primary font-medium">
                    {localSettings?.performance_logic?.[selectedPerformanceRole]?.consistency_weight || 
                     defaultRolePerformance[selectedPerformanceRole]?.consistency_weight}%
                  </span>
                </div>
                <Slider
                  value={[localSettings?.performance_logic?.[selectedPerformanceRole]?.consistency_weight || 
                         defaultRolePerformance[selectedPerformanceRole]?.consistency_weight]}
                  onValueChange={(v) => setLocalSettings({
                    ...localSettings,
                    performance_logic: {
                      ...localSettings?.performance_logic,
                      [selectedPerformanceRole]: {
                        ...localSettings?.performance_logic?.[selectedPerformanceRole],
                        consistency_weight: v[0]
                      }
                    }
                  })}
                  max={100}
                  step={5}
                />
                <p className="text-xs text-muted-foreground">
                  Regular task completion vs sporadic bursts
                </p>
              </div>

              {/* Total check */}
              <div className="pt-4 border-t border-white/10">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Total Weight for {selectedPerformanceRole === 'project_lead' ? 'Project Lead' : selectedPerformanceRole === 'project_manager' ? 'Project Manager' : 'Contributor'}</span>
                  {(() => {
                    const role = selectedPerformanceRole;
                    const weights = localSettings?.performance_logic?.[role] || defaultRolePerformance[role];
                    const total = (weights?.completion_weight || 0) + (weights?.on_time_weight || 0) + 
                                  (weights?.engagement_weight || 0) + (weights?.consistency_weight || 0);
                    return (
                      <span className={`font-bold ${total === 100 ? 'text-chart-3' : 'text-destructive'}`}>
                        {total}%
                      </span>
                    );
                  })()}
                </div>
                {(() => {
                  const role = selectedPerformanceRole;
                  const weights = localSettings?.performance_logic?.[role] || defaultRolePerformance[role];
                  const total = (weights?.completion_weight || 0) + (weights?.on_time_weight || 0) + 
                                (weights?.engagement_weight || 0) + (weights?.consistency_weight || 0);
                  return total !== 100 ? (
                    <p className="text-xs text-destructive mt-2 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Weights should sum to 100%
                    </p>
                  ) : null;
                })()}
              </div>
            </CardContent>
          </Card>

          {/* Role Comparison Summary */}
          <Card className="glass">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Role Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div className="font-medium">Metric</div>
                <div className="text-center">Lead</div>
                <div className="text-center">Manager</div>
                <div className="text-center">Contributor</div>
                
                <div>Completion</div>
                <div className="text-center text-primary">{localSettings?.performance_logic?.project_lead?.completion_weight || defaultRolePerformance.project_lead.completion_weight}%</div>
                <div className="text-center text-primary">{localSettings?.performance_logic?.project_manager?.completion_weight || defaultRolePerformance.project_manager.completion_weight}%</div>
                <div className="text-center text-primary">{localSettings?.performance_logic?.contributor?.completion_weight || defaultRolePerformance.contributor.completion_weight}%</div>
                
                <div>On-Time</div>
                <div className="text-center">{localSettings?.performance_logic?.project_lead?.on_time_weight || defaultRolePerformance.project_lead.on_time_weight}%</div>
                <div className="text-center">{localSettings?.performance_logic?.project_manager?.on_time_weight || defaultRolePerformance.project_manager.on_time_weight}%</div>
                <div className="text-center">{localSettings?.performance_logic?.contributor?.on_time_weight || defaultRolePerformance.contributor.on_time_weight}%</div>
                
                <div>Engagement</div>
                <div className="text-center">{localSettings?.performance_logic?.project_lead?.engagement_weight || defaultRolePerformance.project_lead.engagement_weight}%</div>
                <div className="text-center">{localSettings?.performance_logic?.project_manager?.engagement_weight || defaultRolePerformance.project_manager.engagement_weight}%</div>
                <div className="text-center">{localSettings?.performance_logic?.contributor?.engagement_weight || defaultRolePerformance.contributor.engagement_weight}%</div>
                
                <div>Consistency</div>
                <div className="text-center">{localSettings?.performance_logic?.project_lead?.consistency_weight || defaultRolePerformance.project_lead.consistency_weight}%</div>
                <div className="text-center">{localSettings?.performance_logic?.project_manager?.consistency_weight || defaultRolePerformance.project_manager.consistency_weight}%</div>
                <div className="text-center">{localSettings?.performance_logic?.contributor?.consistency_weight || defaultRolePerformance.contributor.consistency_weight}%</div>
              </div>
            </CardContent>
          </Card>

          <Button onClick={handleSaveSettings} disabled={isSaving} className="gap-2">
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Performance Logic
          </Button>
        </TabsContent>
      </Tabs>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Create a new user account. They will receive login credentials.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="John Doe"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="john@company.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              />
            </div>
            
            {/* Department Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Department</Label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 text-xs text-primary"
                  onClick={() => setShowNewDeptForm(!showNewDeptForm)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {showNewDeptForm ? 'Cancel' : 'New Dept'}
                </Button>
              </div>
              
              {showNewDeptForm ? (
                <div className="space-y-2 p-3 rounded-lg bg-secondary/30 border border-white/10">
                  <Input
                    placeholder="Department name"
                    value={newDeptName}
                    onChange={(e) => setNewDeptName(e.target.value)}
                    className="h-8"
                  />
                  <Select value={selectedTeamForDept} onValueChange={setSelectedTeamForDept}>
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent>
                      {teams.map(team => (
                        <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={handleCreateDepartmentInline} className="w-full h-8">
                    <Building2 className="h-3 w-3 mr-2" />
                    Create Department
                  </Button>
                </div>
              ) : (
                <Select 
                  value={newUser.department_id || 'none'} 
                  onValueChange={(v) => setNewUser({ ...newUser, department_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger>
                    <Building2 className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Department</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Make Admin</Label>
              <Switch
                checked={newUser.is_admin}
                onCheckedChange={(checked) => setNewUser({ ...newUser, is_admin: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser}>
              <Plus className="h-4 w-4 mr-2" />
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user details and permissions.
            </DialogDescription>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Admin Access</Label>
                <Switch
                  checked={editingUser.is_admin}
                  onCheckedChange={(checked) => setEditingUser({ ...editingUser, is_admin: checked })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>
              Cancel
            </Button>
            <Button onClick={() => handleUpdateUser(editingUser.id, editingUser)}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
